/**
 * La liste des conversations (docs/13 §4).
 *
 * Triée par activité — le serveur s'en charge — et non par date de création :
 * une conversation vieille d'un an remonte en tête dès qu'on y écrit, et c'est
 * ce qui interdit de la paginer sur l'identifiant.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useConversations, type Conversation } from "@/features/messaging/api/useMessaging";
import { useSessionStore } from "@/shared/auth/session";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function ConversationsPage() {
  const { t, i18n } = useTranslation("messaging");
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isPending, isError, error } = useConversations(includeArchived);
  const userId = useSessionStore((state) => state.user?.id);

  return (
    <section className="mx-auto max-w-3xl px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{t("title")}</h1>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => {
              setIncludeArchived(event.target.checked);
            }}
          />
          {t("list.includeArchived")}
        </label>
      </header>

      {isPending ? <Skeleton className="h-40 w-full" label={t("title")} /> : null}
      {isError ? <ErrorState error={error} /> : null}

      {data !== undefined ? (
        <Card className="overflow-hidden p-0">
          {data.items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">{t("list.empty")}</p>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {data.items.map((conversation) => (
                // Les marqueurs de test sont sur le `li` et non sur le `Link` :
                // le lien est un composant du routeur, et ce qu'on veut
                // désigner est la ligne — y compris là où le routeur est
                // remplacé par un double.
                <li
                  key={conversation.id}
                  data-testid="conversation-row"
                  data-unread={conversation.unread_count > 0}
                >
                  <Link
                    to="/messages/$conversationId"
                    params={{ conversationId: conversation.id }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-900"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                        {counterpartName(conversation, userId, t("list.unknownParticipant"))}
                      </p>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                        {preview(conversation, t("message.deleted"), t("list.noMessage"))}
                      </p>
                    </div>
                    {conversation.last_message_at !== null ? (
                      <time
                        dateTime={conversation.last_message_at}
                        className="shrink-0 text-xs text-slate-500 dark:text-slate-400"
                      >
                        {new Intl.DateTimeFormat(i18n.language, { dateStyle: "short" }).format(
                          new Date(conversation.last_message_at),
                        )}
                      </time>
                    ) : null}
                    {conversation.unread_count > 0 ? (
                      <span
                        className="shrink-0 rounded-full bg-red-600 px-2 text-xs font-semibold text-white"
                        data-testid="conversation-unread"
                      >
                        {conversation.unread_count}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </section>
  );
}

/**
 * Le nom de l'autre.
 *
 * Une conversation directe a deux participants et l'appelant en est un ; on
 * affiche donc **celui qui n'est pas soi**. Le repli couvre le compte anonymisé
 * par le balayage RGPD, dont l'annuaire ne rend plus rien — afficher son UUID
 * serait pire que « — ».
 */
function counterpartName(
  conversation: Conversation,
  userId: string | undefined,
  fallback: string,
): string {
  const other = conversation.participants.find((participant) => participant.user_id !== userId);
  return other?.display_name ?? fallback;
}

function preview(conversation: Conversation, deletedLabel: string, emptyLabel: string): string {
  if (conversation.last_message === null) {
    return emptyLabel;
  }
  // `is_deleted` d'abord : le corps est nul dans ce cas, et tester le corps
  // ferait afficher « aucun message » sur un fil qui en a bel et bien un.
  if (conversation.last_message.is_deleted) {
    return deletedLabel;
  }
  return conversation.last_message.body ?? emptyLabel;
}
